import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FlowLogo } from '../components/FlowLogo'
import SEO from '../components/SEO'

const Privacy: React.FC = () => {
  const { t } = useTranslation()
  
  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])
  
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Privacy Policy - MortgageFlow"
        description="Read MortgageFlow's privacy policy to understand how we collect, use, and protect your personal information and data."
        url="https://mortgageflow.pro/privacy"
        noindex={false}
      />
      
      {/* Header */}
      <nav className="relative z-50 px-6 py-5 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3">
            <FlowLogo size="md" animate={false} />
            <div>
              <h1 className="text-lg font-semibold text-gray-900">MortgageFlow</h1>
            </div>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center px-5 py-2 text-gray-600 hover:text-gray-900 font-medium transition-colors duration-200"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('legal.privacy.title')}
          </h1>
          <p className="text-gray-600 mb-8">
            {t('legal.privacy.lastUpdated')}: {t('legal.privacy.updateDate')}
          </p>

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.privacy.introduction.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.privacy.introduction.p1')}
            </p>
            <p className="text-gray-700 leading-relaxed">
              {t('legal.privacy.introduction.p2')}
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.privacy.collection.title')}
            </h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              {t('legal.privacy.collection.personal.title')}
            </h3>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed mb-4 space-y-2">
              <li>{t('legal.privacy.collection.personal.item1')}</li>
              <li>{t('legal.privacy.collection.personal.item2')}</li>
              <li>{t('legal.privacy.collection.personal.item3')}</li>
              <li>{t('legal.privacy.collection.personal.item4')}</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              {t('legal.privacy.collection.client.title')}
            </h3>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed mb-4 space-y-2">
              <li>{t('legal.privacy.collection.client.item1')}</li>
              <li>{t('legal.privacy.collection.client.item2')}</li>
              <li>{t('legal.privacy.collection.client.item3')}</li>
              <li>{t('legal.privacy.collection.client.item4')}</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              {t('legal.privacy.collection.usage.title')}
            </h3>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed mb-4 space-y-2">
              <li>{t('legal.privacy.collection.usage.item1')}</li>
              <li>{t('legal.privacy.collection.usage.item2')}</li>
              <li>{t('legal.privacy.collection.usage.item3')}</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.privacy.usage.title')}
            </h2>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed space-y-2">
              <li>{t('legal.privacy.usage.item1')}</li>
              <li>{t('legal.privacy.usage.item2')}</li>
              <li>{t('legal.privacy.usage.item3')}</li>
              <li>{t('legal.privacy.usage.item4')}</li>
              <li>{t('legal.privacy.usage.item5')}</li>
              <li>{t('legal.privacy.usage.item6')}</li>
              <li>{t('legal.privacy.usage.item7')}</li>
            </ul>
          </section>

          {/* Data Security */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.privacy.security.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.privacy.security.p1')}
            </p>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed space-y-2">
              <li>{t('legal.privacy.security.item1')}</li>
              <li>{t('legal.privacy.security.item2')}</li>
              <li>{t('legal.privacy.security.item3')}</li>
              <li>{t('legal.privacy.security.item4')}</li>
              <li>{t('legal.privacy.security.item5')}</li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.privacy.sharing.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.privacy.sharing.p1')}
            </p>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed space-y-2">
              <li>{t('legal.privacy.sharing.item1')}</li>
              <li>{t('legal.privacy.sharing.item2')}</li>
              <li>{t('legal.privacy.sharing.item3')}</li>
              <li>{t('legal.privacy.sharing.item4')}</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.privacy.rights.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.privacy.rights.p1')}
            </p>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed space-y-2">
              <li>{t('legal.privacy.rights.item1')}</li>
              <li>{t('legal.privacy.rights.item2')}</li>
              <li>{t('legal.privacy.rights.item3')}</li>
              <li>{t('legal.privacy.rights.item4')}</li>
              <li>{t('legal.privacy.rights.item5')}</li>
              <li>{t('legal.privacy.rights.item6')}</li>
            </ul>
          </section>

          {/* Cookies */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.privacy.cookies.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.privacy.cookies.p1')}
            </p>
            <p className="text-gray-700 leading-relaxed">
              {t('legal.privacy.cookies.p2')}
            </p>
          </section>

          {/* Data Retention */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.privacy.retention.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed">
              {t('legal.privacy.retention.p1')}
            </p>
          </section>

          {/* Changes to Privacy Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.privacy.changes.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed">
              {t('legal.privacy.changes.p1')}
            </p>
          </section>

          {/* Contact */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('legal.privacy.contact.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('legal.privacy.contact.p1')}
            </p>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <p className="text-gray-700">
                <strong>MortgageFlow.pro</strong><br />
                {t('legal.privacy.contact.email')}: <a href="mailto:privacy@mortgageflow.pro" className="text-gray-900 hover:text-blue-700">privacy@mortgageflow.pro</a><br />
                {t('legal.privacy.contact.address')}: {t('legal.privacy.contact.addressValue')}
              </p>
            </div>
          </section>

          {/* Privacy Officer — Law 25 / PPIPSA requirement */}
          <section className="mb-8 border-t border-gray-200 pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Privacy Officer / Responsable de la protection des renseignements personnels
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Pursuant to Quebec Law 25 (Act respecting the protection of personal information in the private sector —
              PPIPSA) and Canada's Personal Information Protection and Electronic Documents Act (PIPEDA), MortgageFlow
              has designated a Privacy Officer responsible for the protection of personal information within the organisation.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4 italic">
              En vertu de la Loi 25 (Loi sur la protection des renseignements personnels dans le secteur privé) et de
              la Loi sur la protection des renseignements personnels et les documents électroniques (LPRPDE), MortgageFlow
              a désigné un responsable de la protection des renseignements personnels.
            </p>
            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <p className="text-gray-800">
                <strong>Privacy Officer / Responsable :</strong> MortgageFlow Privacy Team<br />
                <strong>Email :</strong>{' '}
                <a href="mailto:privacy@mortgageflow.pro" className="text-blue-700 hover:underline">
                  privacy@mortgageflow.pro
                </a>
              </p>
            </div>
          </section>

          {/* Quebec Law 25 / PPIPSA Compliance */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Quebec Law 25 Compliance / Conformité à la Loi 25
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              MortgageFlow complies with Quebec's Act respecting the protection of personal information in
              the private sector (Law 25 / PPIPSA). As a Quebec-based organisation handling personal
              information of Quebec residents, we uphold the following rights:
            </p>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed space-y-2 mb-4">
              <li><strong>Right of access</strong> — You may request a copy of the personal information we hold about you.</li>
              <li><strong>Right of rectification</strong> — You may request correction of inaccurate or incomplete information.</li>
              <li><strong>Right to erasure</strong> — You may request deletion of your personal information, subject to legal retention obligations (e.g., FINTRAC 5-year records).</li>
              <li><strong>Right to data portability</strong> — You may request your personal information in a structured, commonly used, and machine-readable format.</li>
              <li><strong>Right to withdraw consent</strong> — You may withdraw consent for non-essential processing at any time.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              To exercise any of these rights, contact our Privacy Officer at{' '}
              <a href="mailto:privacy@mortgageflow.pro" className="text-blue-700 hover:underline">
                privacy@mortgageflow.pro
              </a>. We will respond within 30 days.
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Breach Notification / Notification d'incident</h3>
            <p className="text-gray-700 leading-relaxed">
              In the event of a privacy breach that poses a risk of serious injury, MortgageFlow will notify
              the Commission d'accès à l'information (CAI) within 72 hours and will notify affected individuals
              as soon as reasonably possible, as required by Law 25 and PIPEDA.
            </p>
          </section>
        </div>

        {/* Back to home link */}
        <div className="text-center mt-8">
          <Link
            to="/"
            className="inline-flex items-center text-gray-900 hover:text-blue-700 font-medium"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t('legal.backToHome')}
          </Link>
        </div>
      </div>
    </div>
  )
}

export default Privacy

